import {
	ColumnBuilderConfig,
	ColumnMeta,
	ColumnWithSubColumnsBuilder,
	DefaultColumnBuilder,
	RenderActionsFn,
} from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.types';
import {
	getColumnMeta,
	getColumnWithMeta,
} from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.utils';
import { Column, ColumnDef, Row, createColumnHelper, Table } from '@tanstack/react-table';

export class TableColumnBuilder<TData, TValue = unknown> {
	private columns: ColumnDef<TData, TValue>[] = [];
	private config: ColumnBuilderConfig<TData>;
	private columnHelper = createColumnHelper<TData>();

	constructor(config: ColumnBuilderConfig<TData>) {
		this.config = config;
	}

	/**
	 * Add a default column
	 */
	addDefaultColumn(builder: DefaultColumnBuilder<TData>): this {
		const column = this.columnHelper.accessor(builder.accessor, {
			id: builder.id,
			header: () => {
				const HeaderCell = this.config.components?.HeaderCell;
				if (!HeaderCell) {
					throw new Error('HeaderCell component not provided in config');
				}

				return (
					<HeaderCell
						value={builder.header}
						enableFilter={this.config.showFilterInputs}
						alignment={builder.alignment}
						filterComponent={builder.filterComponent}
						onFilterChange={builder.onFilterChange}
						isLastColumn={builder.isLastColumn}
					/>
				);
			},
			cell: ({ row, table, column }) => {
				const DefaultCell = this.config.components?.DefaultCell;
				if (!DefaultCell) {
					throw new Error('DefaultCell component not provided in config');
				}

				// Get column index from visible columns
				const columnIndex = table
					.getVisibleFlatColumns()
					.findIndex((c) => c.id === column.id);

				return (
					<DefaultCell
						value={builder.accessor(row.original)}
						formatter={builder.formatter}
						alignment={builder.alignment}
						isLastRow={this.isLastRow(row, table)}
						isLastColumn={builder.isLastColumn}
						row={row as Row<TData>}
						columnIndex={columnIndex}
						renderActions={this.combineActions(builder.renderActions)}
					/>
				);
			},
			filterFn: builder.filterFn,
			meta: {
				alignment: builder.alignment,
				renderActions: this.combineActions(builder.renderActions),
			} as ColumnMeta<TData>,
		});

		this.columns.push(column);
		return this;
	}

	/**
	 * Add a column with expandable sub-columns
	 */
	addColumnWithSubColumns(builder: ColumnWithSubColumnsBuilder<TData>): this {
		const subColumnIds = builder.subColumns?.map((sub) => sub.id) ?? [];

		// Add parent column
		const parentColumn = this.columnHelper.accessor(builder.accessor, {
			id: builder.id,
			header: ({ column, table }) => {
				const HeaderCell = this.config.components?.HeaderCell;
				if (!HeaderCell) {
					throw new Error('HeaderCell component not provided in config');
				}

				return (
					<HeaderCell
						value={builder.header}
						enableFilter={this.config.showFilterInputs}
						enableExpanding={builder.enableExpanding}
						isExpanded={this.config.hasVisibleSubColumns?.(column, table)}
						onToggleClick={() => this.config.toggleSubColumns?.(column, table)}
						alignment={builder.alignment}
						filterComponent={builder.filterComponent}
						onFilterChange={builder.onFilterChange}
						isLastColumn={builder.isLastColumn}
					/>
				);
			},
			cell: ({ table, row, column }) => {
				const DefaultCell = this.config.components?.DefaultCell;
				if (!DefaultCell) {
					throw new Error('DefaultCell component not provided in config');
				}

				// Get column index from visible columns
				const columnIndex = table
					.getVisibleFlatColumns()
					.findIndex((c) => c.id === column.id);

				return (
					<DefaultCell
						value={builder.accessor(row.original)}
						formatter={builder.formatter}
						alignment={builder.alignment}
						isLastRow={this.isLastRow(row, table)}
						row={row as Row<TData>}
						isLastColumn={builder.isLastColumn}
						columnIndex={columnIndex}
						renderActions={this.combineActions(builder.renderActions)}
					/>
				);
			},
			meta: {
				subColumns: subColumnIds,
				alignment: builder.alignment,
				renderActions: this.combineActions(builder.renderActions),
			} as ColumnMeta<TData>,
		});

		this.columns.push(parentColumn);

		// Add sub-columns with automatic parent linking
		builder.subColumns?.forEach((subBuilder) => {
			const subColumn = this.columnHelper.accessor(subBuilder.accessor, {
				id: subBuilder.id,
				header: ({ table }) => {
					const HeaderCell = this.config.components?.HeaderCell;
					if (!HeaderCell) {
						throw new Error('HeaderCell component not provided in config');
					}

					const subColumn = getColumnWithMeta(table, subBuilder.id);
					if (!subColumn) {
						throw new Error(
							'Sub-column not present when building column with sub-columns'
						);
					}

					const isLastSubColumn = this.config.isLastSubColumn?.(
						subBuilder.id,
						table,
						subColumn.columnDef.meta
					);

					return (
						<HeaderCell
							value={subBuilder.header}
							enableFilter={this.config.showFilterInputs}
							isSubColumn
							isLastSubColumn={isLastSubColumn}
							alignment={subBuilder.alignment}
							filterComponent={subBuilder.filterComponent}
							onFilterChange={subBuilder.onFilterChange}
						/>
					);
				},
				cell: ({ row, table, column }) => {
					const DefaultCell = this.config.components?.DefaultCell;
					if (!DefaultCell) {
						throw new Error('DefaultCell component not provided in config');
					}

					const subColumn = table.getColumn(subBuilder.id);
					if (!subColumn) {
						throw new Error(
							`Sub-column "${subBuilder.id}" not found in table during column definition creation. ` +
								`The sub-column is expected to be present when creating it's own ColumnDef.`
						);
					}

					const meta = getColumnMeta(subColumn);
					const isLastSubColumn = this.config.isLastSubColumn?.(
						subBuilder.id,
						table,
						meta
					);

					// Get column index from visible columns
					const columnIndex = table
						.getVisibleFlatColumns()
						.findIndex((c) => c.id === column.id);

					return (
						<DefaultCell
							value={subBuilder.accessor(row.original)}
							isSubColumn
							isLastSubColumn={isLastSubColumn}
							formatter={subBuilder.formatter}
							alignment={subBuilder.alignment}
							isLastRow={this.isLastRow(row, table)}
							row={row as Row<TData>}
							isLastColumn={builder.isLastColumn}
							columnIndex={columnIndex}
							renderActions={this.combineActions(subBuilder.renderActions)}
						/>
					);
				},
				meta: {
					parentId: builder.id,
					isSubColumn: true,
					alignment: subBuilder.alignment,
					renderActions: this.combineActions(subBuilder.renderActions),
				} as ColumnMeta<TData>,
			});

			this.columns.push(subColumn);
		});

		return this;
	}

	/**
	 * Add a column with a custom cell component
	 */
	addColumnWithCustomCell<TCellProps extends object>(
		builder: DefaultColumnBuilder<TData> & {
			cellComponent: React.ComponentType<TCellProps>;
			cellPropsMapper: (data: {
				row: TData;
				tanstackRow: Row<TData>;
				table: Table<TData>;
				column: Column<TData>;
				value: unknown;
				isLastRow: boolean;
				isLastColumn?: boolean;
				columnIndex: number;
			}) => TCellProps;
		}
	): this {
		const column = this.columnHelper.accessor(builder.accessor, {
			id: builder.id,
			header: () => {
				const HeaderCell = this.config.components?.HeaderCell;
				if (!HeaderCell) {
					throw new Error('HeaderCell component not provided in config');
				}

				return (
					<HeaderCell
						value={builder.header}
						enableFilter={this.config.showFilterInputs}
						alignment={builder.alignment}
						filterComponent={builder.filterComponent}
						onFilterChange={builder.onFilterChange}
					/>
				);
			},
			cell: ({ row, table, column }) => {
				const CellComponent = builder.cellComponent;
				const value = builder.accessor(row.original);
				const isLastRow = this.isLastRow(row, table);

				// Get column index from visible columns
				const columnIndex = table
					.getVisibleFlatColumns()
					.findIndex((c) => c.id === column.id);

				const cellProps = builder.cellPropsMapper({
					row: row.original,
					tanstackRow: row,
					column,
					table,
					value,
					isLastRow,
					isLastColumn: builder.isLastColumn,
					columnIndex,
				});

				return <CellComponent {...cellProps} />;
			},
			filterFn: builder.filterFn,
			meta: {
				alignment: builder.alignment,
			} as ColumnMeta<TData>,
		});

		this.columns.push(column);
		return this;
	}

	/**
	 * Build and return the column definitions
	 */
	build(): ColumnDef<TData>[] {
		return [...this.columns];
	}

	private combineActions(columnActions?: RenderActionsFn): RenderActionsFn | undefined {
		const defaultActions = this.config.defaultRenderActions;
		if (!defaultActions && !columnActions) {
			return undefined;
		}
		if (!columnActions) {
			return defaultActions;
		}
		if (!defaultActions) {
			return columnActions;
		}
		return (row, value, isHovered, formatter) => (
			<>
				{defaultActions(row, value, isHovered, formatter)}
				{columnActions(row, value, isHovered, formatter)}
			</>
		);
	}

	/**
	 * Helper method to determine if a row is the last row
	 */
	private isLastRow(
		row: { index: number },
		table: { getRowModel: () => { rows: unknown[] } }
	): boolean {
		const lastRow = table.getRowModel().rows[table.getRowModel().rows.length - 1];
		return row.index === (lastRow as { index: number }).index;
	}
}
